import { useLocation, useSearchParams } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import {
  decideStickyLocation,
  readStickyLocation,
  rememberStickyLocation,
  type UrlSnapshot,
} from 'app/lib/stickyLocation'
import { useAuthStore } from 'app/stores/StoreContext'
import { useHandleSearchParams } from 'app/utils'

/**
 * Keeps the per-device location selection in step with the URL.
 *
 * WorkspaceGate calls it once and passes its own `resolved` state: before the
 * gate has settled a mismatched `wId` an injection would land on a URL that
 * is about to switch workspace and reload, and the other workspace would then
 * remember it.
 *
 * Writes go through useHandleSearchParams because it reads window.location,
 * which is where WorkspaceGate stamps `wId`; React Router's own snapshot can
 * lag behind it and re-stamp a stale workspace.
 */
export function useStickyLocationSync(enabled: boolean): void {
  const { pathname, search } = useLocation()
  const workspaceId = useAuthStore().user?.workspaceId
  const handleSearchParams = useHandleSearchParams()
  const previousRef = useRef<UrlSnapshot | null>(null)

  useEffect(() => {
    if (!enabled) return
    const current = { pathname, search }
    const decision = decideStickyLocation({
      previous: previousRef.current,
      current,
      remembered: readStickyLocation(workspaceId),
    })
    previousRef.current = current
    if (decision.action === `remember`) rememberStickyLocation(workspaceId, decision.ids)
    if (decision.action === `forget`) rememberStickyLocation(workspaceId, [])
    if (decision.action === `inject`) handleSearchParams(decision.params, { replace: true })
  }, [enabled, pathname, search, workspaceId])
}

/**
 * The remembered location for a page that applies it as one of its tab's
 * predefined filters (Contacts, Activities). Empty once the URL carries a
 * `filters` param of its own, because that param replaces the tab defaults
 * wholesale and the user's own selection, or lack of one, is in there.
 */
export function useStickyLocationDefault(): string[] {
  const [searchParams] = useSearchParams()
  const workspaceId = useAuthStore().user?.workspaceId
  return searchParams.has(`filters`) ? [] : readStickyLocation(workspaceId)
}
